import type { Field } from "model/fieldTypes";
import type { MoveEvent } from "model/useAnimalGroupQueries";

// ─── Types ───────────────────────────────────────────────────────────────────

export interface ClashInfo {
  eventA: {
    fieldGuid: string;
    fieldName: string;
    dateIn: string;
    dateOut: string;
  };
  eventB: {
    fieldGuid: string;
    fieldName: string;
    dateIn: string;
    dateOut: string;
  };
  overlapDays: number;
}

export interface FieldGrazingEstimate {
  fieldGuid: string;
  fieldName: string;
  availableDM_kg: number;
  dailyDemand_kg: number;
  estimatedDays: number;
}

export interface RecoveryEstimate {
  fieldGuid: string;
  fieldName: string;
  actualRecoveryDays: number | null;
  idealRecoveryDays: number;
  isSufficient: boolean | null;
}

export interface LikelyPathResult {
  mainSequence: MoveEvent[];
  outliers: MoveEvent[];
  coverageDays: number;
}

export interface SuggestedEvent {
  fieldGuid: string;
  fieldName: string;
  suggestedDateIn: Date;
  suggestedDateOut: Date;
  estimatedDays: number;
}

/** A group of events that share the same start date and need time staggering. */
export interface TimelineFix {
  /** The shared start date (ISO string, date portion) */
  sharedDate: string;
  /** Events in their suggested order, with corrected dateTimeIn/dateTimeOut */
  events: {
    moveEvent: MoveEvent;
    originalDateIn: string;
    originalDateOut: string | null;
    suggestedDateIn: Date;
    suggestedDateOut: Date;
    suggestedDays: number;
  }[];
}

export interface RotationAnalysis {
  clashes: ClashInfo[];
  timelineFixes: TimelineFix[];
  fieldEstimates: FieldGrazingEstimate[];
  recoveryEstimates: RecoveryEstimate[];
  likelyPath: LikelyPathResult;
  suggestedRotation: SuggestedEvent[];
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const MS_PER_DAY = 86_400_000;

function isInSeason(dateStr: string, seasonYear: number): boolean {
  return new Date(dateStr).getFullYear() === seasonYear;
}

function effectiveEnd(event: MoveEvent): Date {
  return event.dateTimeOut ? new Date(event.dateTimeOut) : new Date();
}

function durationDays(start: Date, end: Date): number {
  return Math.max(0, (end.getTime() - start.getTime()) / MS_PER_DAY);
}

function filterSeasonEvents(
  events: MoveEvent[],
  seasonYear: number
): MoveEvent[] {
  return events.filter(
    (e) =>
      e.fieldGuid != null &&
      e.fieldName != null &&
      isInSeason(e.dateTimeIn, seasonYear)
  );
}

// ─── 1. Detect Clashes ──────────────────────────────────────────────────────

/**
 * Finds pairs of events that overlap in time on different fields,
 * meaning an animal group was assigned to two fields simultaneously.
 */
export function detectClashes(
  events: MoveEvent[],
  seasonYear: number
): ClashInfo[] {
  const filtered = filterSeasonEvents(events, seasonYear).sort(
    (a, b) =>
      new Date(a.dateTimeIn).getTime() - new Date(b.dateTimeIn).getTime()
  );

  const clashes: ClashInfo[] = [];

  for (let i = 0; i < filtered.length; i++) {
    const a = filtered[i];
    const aStart = new Date(a.dateTimeIn);
    const aEnd = effectiveEnd(a);

    for (let j = i + 1; j < filtered.length; j++) {
      const b = filtered[j];
      const bStart = new Date(b.dateTimeIn);
      const bEnd = effectiveEnd(b);

      // Since sorted by dateTimeIn, if bStart >= aEnd there's no overlap
      // and no further j will overlap either.
      if (bStart >= aEnd) break;

      // Same field is not a clash
      if (a.fieldGuid === b.fieldGuid) continue;

      const overlapStart = bStart > aStart ? bStart : aStart;
      const overlapEnd = aEnd < bEnd ? aEnd : bEnd;
      const overlapDays = durationDays(overlapStart, overlapEnd);

      // Allow same-day grazing (morning/evening): ignore overlaps < 0.5 day
      if (overlapDays > 0.5) {
        clashes.push({
          eventA: {
            fieldGuid: a.fieldGuid ?? "",
            fieldName: a.fieldName ?? "",
            dateIn: a.dateTimeIn,
            dateOut: aEnd.toISOString(),
          },
          eventB: {
            fieldGuid: b.fieldGuid ?? "",
            fieldName: b.fieldName ?? "",
            dateIn: b.dateTimeIn,
            dateOut: bEnd.toISOString(),
          },
          overlapDays: Math.round(overlapDays * 100) / 100,
        });
      }
    }
  }

  return clashes;
}

// ─── 2. Estimate Grazing Duration ───────────────────────────────────────────

/**
 * For each field, estimates how many days of grazing are available
 * based on current dry matter, post-grazing target, and daily demand.
 *
 * Both dryMatterNow and postGrazingTarget are in kg/ha. Area is in m².
 * Available DM (total kg) = (dryMatterNow - postGrazingTarget) * area / 10000.
 */
export function estimateGrazingDuration(
  fields: Field[],
  postGrazingTarget: number,
  dailyDemand: number
): FieldGrazingEstimate[] {
  return fields.map((field) => {
    const availableDM_kg = Math.max(
      0,
      ((field.dryMatterNow - postGrazingTarget) * field.area) / 10000
    );

    const estimatedDays =
      dailyDemand <= 0 ? Infinity : Math.max(0, availableDM_kg / dailyDemand);

    return {
      fieldGuid: field.guid,
      fieldName: field.name,
      availableDM_kg: Math.round(availableDM_kg * 100) / 100,
      dailyDemand_kg: dailyDemand,
      estimatedDays: Math.round(estimatedDays * 100) / 100,
    };
  });
}

// ─── 3. Estimate Recovery Times ─────────────────────────────────────────────

/**
 * For each field, measures the actual rest period between consecutive grazing
 * events and compares it to an ideal recovery time derived from the number of
 * fields and average grazing duration.
 */
export function estimateRecoveryTimes(
  fields: Field[],
  events: MoveEvent[],
  fieldEstimates: FieldGrazingEstimate[],
  seasonYear: number
): RecoveryEstimate[] {
  const avgGrazingDays =
    fieldEstimates.length > 0
      ? fieldEstimates.reduce(
          (sum, e) =>
            sum + (Number.isFinite(e.estimatedDays) ? e.estimatedDays : 0),
          0
        ) / fieldEstimates.length
      : 0;

  // Ideal recovery: the other fields each need to be grazed once before returning
  const idealRecoveryDays =
    Math.round((fields.length - 1) * avgGrazingDays * 100) / 100;

  const filtered = filterSeasonEvents(events, seasonYear).sort(
    (a, b) =>
      new Date(a.dateTimeIn).getTime() - new Date(b.dateTimeIn).getTime()
  );

  // Group events by field
  const eventsByField = new Map<string, MoveEvent[]>();
  for (const e of filtered) {
    const key = e.fieldGuid ?? "";
    if (!eventsByField.has(key)) eventsByField.set(key, []);
    eventsByField.get(key)?.push(e);
  }

  return fields.map((field) => {
    const fieldEvents = eventsByField.get(field.guid) ?? [];

    if (fieldEvents.length < 2) {
      return {
        fieldGuid: field.guid,
        fieldName: field.name,
        actualRecoveryDays: null,
        idealRecoveryDays,
        isSufficient: null,
      };
    }

    // Measure gaps between consecutive events on this field
    const gaps: number[] = [];
    for (let i = 1; i < fieldEvents.length; i++) {
      const prevEnd = effectiveEnd(fieldEvents[i - 1]);
      const nextStart = new Date(fieldEvents[i].dateTimeIn);
      const gap = durationDays(prevEnd, nextStart);
      if (gap > 0) gaps.push(gap);
    }

    const actualRecoveryDays =
      gaps.length > 0
        ? Math.round((gaps.reduce((s, g) => s + g, 0) / gaps.length) * 100) /
          100
        : null;

    return {
      fieldGuid: field.guid,
      fieldName: field.name,
      actualRecoveryDays,
      idealRecoveryDays,
      isSufficient:
        actualRecoveryDays !== null
          ? actualRecoveryDays >= idealRecoveryDays
          : null,
    };
  });
}

// ─── 4. Find Likely Path (Weighted Interval Scheduling) ─────────────────────

/**
 * Uses weighted interval scheduling dynamic programming to find the
 * maximum-weight subset of non-overlapping events, where weight = duration
 * in days. This identifies the "main" rotation path versus outlier events.
 */
export function findLikelyPath(
  events: MoveEvent[],
  seasonYear: number
): LikelyPathResult {
  const filtered = filterSeasonEvents(events, seasonYear).sort(
    (a, b) => effectiveEnd(a).getTime() - effectiveEnd(b).getTime()
  );

  if (filtered.length === 0) {
    return { mainSequence: [], outliers: [], coverageDays: 0 };
  }

  const n = filtered.length;
  const starts = filtered.map((e) => new Date(e.dateTimeIn).getTime());
  const ends = filtered.map((e) => effectiveEnd(e).getTime());
  const weights = filtered.map((e) =>
    durationDays(new Date(e.dateTimeIn), effectiveEnd(e))
  );

  // For each event i, find the latest event j (j < i) that doesn't overlap with i.
  // Events are sorted by end time, so we need latest j where ends[j] <= starts[i].
  function latestNonOverlapping(idx: number): number {
    let lo = 0;
    let hi = idx - 1;
    let result = -1;
    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      if (ends[mid] <= starts[idx]) {
        result = mid;
        lo = mid + 1;
      } else {
        hi = mid - 1;
      }
    }
    return result;
  }

  // dp[i] = max weight using events 0..i
  const dp = new Array<number>(n);
  dp[0] = weights[0];

  for (let i = 1; i < n; i++) {
    const includeWeight = weights[i];
    const p = latestNonOverlapping(i);
    const inclTotal = includeWeight + (p >= 0 ? dp[p] : 0);
    dp[i] = Math.max(dp[i - 1], inclTotal);
  }

  // Backtrack to find which events are in the optimal set
  const selectedIndices = new Set<number>();
  let i = n - 1;
  while (i >= 0) {
    const p = latestNonOverlapping(i);
    const inclTotal = weights[i] + (p >= 0 ? dp[p] : 0);
    if (i === 0) {
      // Base case: include if its weight is worth it (always is for first element)
      if (weights[i] > 0) selectedIndices.add(i);
      break;
    }
    if (inclTotal >= dp[i - 1]) {
      selectedIndices.add(i);
      i = p;
    } else {
      i--;
    }
  }

  const mainSequence: MoveEvent[] = [];
  const outliers: MoveEvent[] = [];
  let coverageDays = 0;

  for (let idx = 0; idx < n; idx++) {
    if (selectedIndices.has(idx)) {
      mainSequence.push(filtered[idx]);
      coverageDays += weights[idx];
    } else {
      outliers.push(filtered[idx]);
    }
  }

  return {
    mainSequence,
    outliers,
    coverageDays: Math.round(coverageDays * 100) / 100,
  };
}

// ─── 5. Suggest Rotation ────────────────────────────────────────────────────

/**
 * Suggests a forward rotation schedule starting from the last known event,
 * visiting ungrazed fields first, using estimated grazing durations.
 */
export function suggestRotation(
  fields: Field[],
  fieldEstimates: FieldGrazingEstimate[],
  events: MoveEvent[],
  seasonYear: number
): SuggestedEvent[] {
  const filtered = filterSeasonEvents(events, seasonYear);

  // Find the latest event end date as our starting point
  let lastEnd: Date;
  if (filtered.length > 0) {
    const latestEvent = filtered.reduce((latest, e) => {
      const end = effectiveEnd(e);
      return end > effectiveEnd(latest) ? e : latest;
    });
    lastEnd = effectiveEnd(latestEvent);
  } else {
    lastEnd = new Date();
  }

  // Build a set of fields that have been grazed this season
  const grazedFieldGuids = new Set(filtered.map((e) => e.fieldGuid));

  // Build lookup for estimates
  const estimateByGuid = new Map(fieldEstimates.map((e) => [e.fieldGuid, e]));

  // Prioritise ungrazed fields first, then grazed fields
  const ungrazed = fields.filter((f) => !grazedFieldGuids.has(f.guid));
  const grazed = fields.filter((f) => grazedFieldGuids.has(f.guid));
  const orderedFields = [...ungrazed, ...grazed];

  const suggestions: SuggestedEvent[] = [];
  let cursor = new Date(lastEnd.getTime());

  for (const field of orderedFields) {
    const estimate = estimateByGuid.get(field.guid);
    const days =
      estimate && Number.isFinite(estimate.estimatedDays)
        ? estimate.estimatedDays
        : 0;

    if (days <= 0) continue;

    const suggestedDateIn = new Date(cursor.getTime());
    const suggestedDateOut = new Date(cursor.getTime() + days * MS_PER_DAY);

    suggestions.push({
      fieldGuid: field.guid,
      fieldName: field.name,
      suggestedDateIn,
      suggestedDateOut,
      estimatedDays: Math.round(days * 100) / 100,
    });

    cursor = suggestedDateOut;
  }

  return suggestions;
}

// ─── 6. Detect Timeline Sequencing Issues ──────────────────────────────────

/**
 * Holistic timeline fix: walks the full event sequence and finds connected
 * chains of overlapping events, then re-staggers the entire chain.
 *
 * For example, if events overlap like:
 *   Field 19 (Feb 7-10), Field 18 (Feb 7-9), Field 14 (Feb 7-8),
 *   but (Feb 10-13), Field 17 (Feb 14-16)
 * The chain includes all events from Feb 7 through Feb 16 because each
 * overlaps with the next. The fix stagers them sequentially.
 */
export function detectTimelineFixes(
  events: MoveEvent[],
  fieldEstimates: FieldGrazingEstimate[],
  seasonYear: number
): TimelineFix[] {
  // Only consider accepted events — proposed events are reviewed separately
  const filtered = filterSeasonEvents(events, seasonYear)
    .filter((e) => e.verificationStatus !== "Proposed")
    .sort(
      (a, b) =>
        new Date(a.dateTimeIn).getTime() - new Date(b.dateTimeIn).getTime()
    );

  if (filtered.length < 2) return [];

  const estimateByField = new Map(fieldEstimates.map((e) => [e.fieldGuid, e]));

  function getEventDuration(ev: MoveEvent): number {
    const stored = ev.dateTimeOut
      ? durationDays(new Date(ev.dateTimeIn), new Date(ev.dateTimeOut))
      : null;
    if (stored != null && stored > 0) return stored;
    const estimate = estimateByField.get(ev.fieldGuid ?? "");
    if (
      estimate &&
      Number.isFinite(estimate.estimatedDays) &&
      estimate.estimatedDays > 0
    ) {
      return estimate.estimatedDays;
    }
    return 1;
  }

  // Build chains iteratively: stagger events, then check if the new end
  // time pulls in downstream events that weren't originally overlapping.
  const fixes: TimelineFix[] = [];
  const consumed = new Set<number>(); // indices already in a chain

  for (let start = 0; start < filtered.length; start++) {
    if (consumed.has(start)) continue;

    // Seed the chain with the first event
    const chain: MoveEvent[] = [filtered[start]];
    consumed.add(start);
    let chainEnd = effectiveEnd(filtered[start]).getTime();

    // Pass 1: gather overlapping events by original dates
    for (let j = start + 1; j < filtered.length; j++) {
      if (consumed.has(j)) continue;
      const nextStart = new Date(filtered[j].dateTimeIn).getTime();
      if (nextStart <= chainEnd) {
        chain.push(filtered[j]);
        consumed.add(j);
        const thisEnd = effectiveEnd(filtered[j]).getTime();
        if (thisEnd > chainEnd) chainEnd = thisEnd;
      }
    }

    if (chain.length < 2) continue;

    // Pass 2: stagger the chain and check if the new end pulls in more events
    let stable = false;
    while (!stable) {
      stable = true;
      // Compute suggested staggered end
      let cursor = new Date(chain[0].dateTimeIn).getTime();
      for (const ev of chain) {
        cursor += getEventDuration(ev) * MS_PER_DAY;
      }
      const suggestedChainEnd = cursor;

      // Check if any not-yet-consumed event overlaps with the suggested end
      for (let j = start + 1; j < filtered.length; j++) {
        if (consumed.has(j)) continue;
        const nextStart = new Date(filtered[j].dateTimeIn).getTime();
        if (nextStart < suggestedChainEnd) {
          chain.push(filtered[j]);
          consumed.add(j);
          stable = false; // re-check with the new chain
        }
      }
    }

    // Compute final suggested times
    let cursor = new Date(chain[0].dateTimeIn).getTime();
    const fixEvents = chain.map((ev) => {
      const days = getEventDuration(ev);
      const suggestedDateIn = new Date(cursor);
      const suggestedDateOut = new Date(cursor + days * MS_PER_DAY);
      cursor = suggestedDateOut.getTime();

      return {
        moveEvent: ev,
        originalDateIn: ev.dateTimeIn,
        originalDateOut: ev.dateTimeOut,
        suggestedDateIn,
        suggestedDateOut,
        suggestedDays: Math.round(days * 10) / 10,
      };
    });

    // Only include if the suggestion actually changes something
    const hasChanges = fixEvents.some((fe) => {
      const origIn = new Date(fe.originalDateIn).toISOString().slice(0, 10);
      const sugIn = fe.suggestedDateIn.toISOString().slice(0, 10);
      return origIn !== sugIn;
    });

    if (hasChanges) {
      const label = new Date(chain[0].dateTimeIn).toISOString().slice(0, 10);
      fixes.push({ sharedDate: label, events: fixEvents });
    }
  }

  return fixes;
}

// ─── 7. Full Analysis ───────────────────────────────────────────────────────

/**
 * Runs all rotation analysis algorithms and returns a combined result.
 */
export function analyzeRotation(
  fields: Field[],
  events: MoveEvent[],
  postGrazingTarget: number,
  dailyDemand: number,
  seasonYear: number
): RotationAnalysis {
  const clashes = detectClashes(events, seasonYear);
  const fieldEstimates = estimateGrazingDuration(
    fields,
    postGrazingTarget,
    dailyDemand
  );
  const timelineFixes = detectTimelineFixes(events, fieldEstimates, seasonYear);
  const recoveryEstimates = estimateRecoveryTimes(
    fields,
    events,
    fieldEstimates,
    seasonYear
  );
  const likelyPath = findLikelyPath(events, seasonYear);
  const suggestedRotation = suggestRotation(
    fields,
    fieldEstimates,
    events,
    seasonYear
  );

  return {
    clashes,
    timelineFixes,
    fieldEstimates,
    recoveryEstimates,
    likelyPath,
    suggestedRotation,
  };
}
